import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { SiAndroid, SiApple } from 'react-icons/si';
import { useLocation } from 'react-router-dom';
import { playClickSound } from '@/utils/soundUtils';
import { useTranslation } from 'react-i18next';
import { getPostLoginTour, getTourForPath, useTourGuide } from '@/components/TourGuide';
import IosPwaGuideModal from '@/components/IosPwaGuideModal';
import { useDeviceMode } from '@/contexts/DeviceModeContext';

/** Header / accent từ `globals.css`; fallback trùng kiosk khi app không định nghĩa biến. */
const SUPPORT_RAIL_TRACK =
  'border-white/12 bg-[linear-gradient(180deg,var(--Background-Header,#040F29)_0%,color-mix(in_srgb,var(--Background-Header,#040F29)_68%,var(--text-high-light,#22d3ee)_32%)_100%)] shadow-[0_18px_35px_rgba(4,15,41,0.5)]';

const SUPPORT_RAIL_ICON_BUTTON_BASE =
  'group relative flex items-center justify-center rounded-xl border border-transparent bg-transparent transition-all duration-200 hover:bg-white/10';

const SUPPORT_RAIL_ICON_BUTTON_ACTIVE =
  'border-[var(--text-high-light,#22d3ee)]/40 bg-white/10 scale-105';

/** Cùng cỡ với các icon rail khác (Si / Lucide). */
const RAIL_BRAND_ICON = 'mx-auto h-7 w-7';
const RAIL_BRAND_ICON_MOBILE = 'mx-auto h-6 w-6';

/** Collapsed = short arrow tab; expanded = icon rail (column desktop / 2×2 mobile). */
const RAIL_TAB_W = 28;
const RAIL_TAB_H = 40;
const RAIL_MENU_W_DESKTOP = 68;
const RAIL_MENU_W_MOBILE = 104;

const TelegramIcon = ({ compact }: { compact?: boolean }) => (
  <img
    src={`${import.meta.env.BASE_URL}telegram-mark.png`}
    alt=""
    className={`${compact ? RAIL_BRAND_ICON_MOBILE : RAIL_BRAND_ICON} object-contain`}
    draggable={false}
    aria-hidden
  />
);
const TourGuideIcon = ({ compact }: { compact?: boolean }) => (
  <HelpCircle
    className={`${compact ? RAIL_BRAND_ICON_MOBILE : RAIL_BRAND_ICON} text-[var(--text-high-light,#22d3ee)]`}
    aria-hidden
  />
);
const AndroidIcon = ({ compact }: { compact?: boolean }) => (
  <SiAndroid
    className={`${compact ? RAIL_BRAND_ICON_MOBILE : RAIL_BRAND_ICON} text-[#3DDC84]`}
    aria-hidden
  />
);
const AppleIcon = ({ compact }: { compact?: boolean }) => (
  <SiApple
    className={`${compact ? RAIL_BRAND_ICON_MOBILE : RAIL_BRAND_ICON} text-[#f5f5f7]`}
    aria-hidden
  />
);

type SupportActionType = 'tour_guide' | 'telegram' | 'android_apk' | 'ios_guide';

interface SupportActionItem {
  id: SupportActionType;
  label: string;
  icon: React.ComponentType<{ compact?: boolean }>;
  url?: string;
}

/** Routes where the floating rail is hidden (minimal chrome, full-bleed flows). */
const HIDDEN_ROUTE_MATCHERS = ['/login', '/register', '/account/wallet/deposit-checkout'];

export default function SupportActionRail() {
  const location = useLocation();
  const { t } = useTranslation();
  const deviceMode = useDeviceMode();
  const isMobile = deviceMode === 'mobile';
  const { openWelcome, start, close } = useTourGuide();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isIosGuideOpen, setIsIosGuideOpen] = useState(false);
  const [isRailExpanded, setIsRailExpanded] = useState(false);

  const menuWidth = isMobile ? RAIL_MENU_W_MOBILE : RAIL_MENU_W_DESKTOP;

  const actions = useMemo<SupportActionItem[]>(() => {
    const telegramUrl = String(import.meta.env.VITE_CLIENT_TELEGRAM_SUPPORT_URL || '').trim();
    const androidApkUrl = String(import.meta.env.VITE_CLIENT_ANDROID_APK_URL || '').trim();
    const rows: SupportActionItem[] = [
      {
        id: 'telegram',
        label: t('supportRail.telegram.title'),
        icon: TelegramIcon,
        url: telegramUrl,
      },
      {
        id: 'android_apk',
        label: t('supportRail.androidApk.title'),
        icon: AndroidIcon,
        url: androidApkUrl,
      },
      {
        id: 'ios_guide',
        label: t('supportRail.iosGuide.title'),
        icon: AppleIcon,
      },
      {
        id: 'tour_guide',
        label: t('supportRail.tourGuide.title', 'Tour Guide'),
        icon: TourGuideIcon,
      },
    ];
    return rows.filter(
      (item) =>
        item.id === 'tour_guide' ||
        item.id === 'ios_guide' ||
        Boolean(item.url)
    );
  }, [t]);

  const isHiddenRoute = useMemo(
    () => HIDDEN_ROUTE_MATCHERS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  const shouldShowRail = actions.length > 0 && !isHiddenRoute;

  useEffect(() => {
    if (!shouldShowRail) {
      setIsIosGuideOpen(false);
      setIsRailExpanded(false);
    }
  }, [shouldShowRail]);

  useEffect(() => {
    setIsRailExpanded(false);
  }, [location.pathname]);

  const closeIosGuide = (_source: string) => {
    setIsIosGuideOpen(false);
  };

  if (!shouldShowRail) return null;

  const handleSelectAction = (action: SupportActionItem) => {
    playClickSound();
    if (action.id === 'tour_guide') {
      close();
      const tour =
        getTourForPath(location.pathname) ??
        getPostLoginTour() ??
        getTourForPath('/account/settings');
      if (tour) {
        openWelcome(tour.steps);
        start();
      }
      return;
    }
    if (action.id === 'ios_guide') {
      setIsIosGuideOpen(true);
      return;
    }
    if (action.url) {
      window.open(action.url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderActionButton = (action: SupportActionItem, index: number) => {
    const Icon = action.icon;
    const isActive = action.id === 'ios_guide' && isIosGuideOpen;
    return (
      <li key={action.id} className={isMobile ? 'flex items-center justify-center' : undefined}>
        <button
          ref={index === 0 ? triggerRef : undefined}
          type="button"
          tabIndex={isRailExpanded ? 0 : -1}
          onClick={() => handleSelectAction(action)}
          className={`${
            isMobile ? 'h-10 w-10' : 'h-[44px] w-[44px] sm:h-[46px] sm:w-[46px]'
          } ${SUPPORT_RAIL_ICON_BUTTON_BASE} ${isActive ? SUPPORT_RAIL_ICON_BUTTON_ACTIVE : ''}`}
          aria-label={action.label}
        >
          <Icon compact={isMobile} />
        </button>
      </li>
    );
  };

  return (
    <>
      <div className="fixed right-0 top-1/2 z-[85] -translate-y-1/2">
        <div
          className={`flex overflow-hidden rounded-l-[20px] border border-r-0 transition-[min-height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${SUPPORT_RAIL_TRACK} ${
            isRailExpanded ? 'items-stretch' : 'items-center'
          }`}
          style={{
            minHeight: isRailExpanded ? undefined : RAIL_TAB_H,
          }}
        >
          <button
            type="button"
            onClick={() => {
              playClickSound();
              setIsRailExpanded((prev) => !prev);
            }}
            aria-expanded={isRailExpanded}
            aria-controls="support-action-rail-menu"
            aria-label={
              isRailExpanded
                ? t('supportRail.closeMenuAriaLabel')
                : t('supportRail.openMenuAriaLabel')
            }
            className={`flex shrink-0 items-center justify-center text-white/90 transition-colors duration-200 hover:bg-white/10 ${
              isRailExpanded ? 'self-stretch' : ''
            }`}
            style={{
              width: RAIL_TAB_W,
              height: isRailExpanded ? undefined : RAIL_TAB_H,
            }}
          >
            {isRailExpanded ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>

          <div
            id="support-action-rail-menu"
            className="overflow-hidden transition-[max-width,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{
              maxWidth: isRailExpanded ? menuWidth : 0,
              opacity: isRailExpanded ? 1 : 0,
            }}
            aria-hidden={!isRailExpanded}
          >
            <ul
              className={`${
                isMobile
                  ? 'grid w-[104px] grid-cols-2 gap-1.5 px-1.5 py-2'
                  : 'flex w-[68px] flex-col items-center gap-2 px-2 py-3'
              } ${isRailExpanded ? '' : 'pointer-events-none'}`}
            >
              {actions.map((action, index) => renderActionButton(action, index))}
            </ul>
          </div>
        </div>
      </div>

      <IosPwaGuideModal
        isOpen={isIosGuideOpen}
        onClose={() => closeIosGuide('close_button')}
      />
    </>
  );
}
